'use client';

import { useSyncExternalStore } from 'react';

/**
 * Configuration for {@link createLocalStorageStore}.
 */
export interface LocalStorageStoreOptions<T> {
  /** localStorage key. Must include a service prefix (`famfam:`, `wt-web:`) per
   *  the cross-app dev-proxy collision rule. */
  readonly key: string;
  /** Custom event name used to notify in-tab subscribers when the store changes
   *  in this same tab (cross-tab changes ride the native `storage` event). */
  readonly eventName: string;
  /** Parse the raw localStorage string into a typed value. Return `undefined`
   *  to fall through to {@link defaultValue}. */
  parse(raw: string): T | undefined;
  /** Serialize a typed value into the string written to localStorage. */
  serialize(value: T): string;
  /** Value returned when localStorage is unavailable, missing, or
   *  unparseable. Also used as the SSR snapshot to keep server- and
   *  client-rendered markup in sync on first paint. */
  readonly defaultValue: T;
}

/**
 * The store handle returned by {@link createLocalStorageStore}.
 */
export interface LocalStorageStore<T> {
  /** Read the current value (default in non-browser / blocked-storage hosts). */
  read(): T;
  /** Persist a new value and notify in-tab subscribers. */
  set(value: T): void;
  /** Subscribe to changes (in-tab via custom event, cross-tab via `storage`). */
  subscribe(onChange: () => void): () => void;
  /** React hook backed by `useSyncExternalStore`. SSR-safe. */
  useValue(): T;
}

/**
 * Generic factory for a localStorage-backed store wired into React via
 * `useSyncExternalStore`. Centralizes the read/set/subscribe/use pattern so
 * each consumer doesn't reimplement the try/catch + custom-event dance.
 *
 * - localStorage access is wrapped in try/catch so Safari private browsing,
 *   third-party-cookie restrictions, and quota errors fall through to the
 *   default rather than crashing the host component.
 * - `set()` always dispatches the change event, even when the write throws,
 *   so in-tab subscribers stay coherent with the in-memory snapshot returned
 *   by `read()`.
 * - The SSR snapshot is the configured default — pre-mount renders match the
 *   server output, and the post-mount commit picks up the real persisted value.
 * - Parsed snapshots are cached by raw storage value, so object-valued parsers
 *   satisfy `useSyncExternalStore`'s referential-stability requirement.
 *
 * Browser storage keys must include a service prefix (`famfam:`, `wt-web:`)
 * per `.claude/rules/typescript.md` § "Conventions" so apps proxied through
 * `dev-proxy` don't collide on shared keys.
 */
export function createLocalStorageStore<T>(options: LocalStorageStoreOptions<T>): LocalStorageStore<T> {
  const {
    key,
    eventName,
    parse,
    serialize,
    defaultValue
  } = options;
  let snapshotCache: {
    raw: string | null;
    value: T;
  } | null = null;

  /** Returns the current persisted value, falling back to the configured default when storage is unavailable or the value is unparseable. Reuses the parsed snapshot while the raw storage value is unchanged. */
  function read(): T {
    if (typeof window === 'undefined') return defaultValue;
    try {
      const raw = window.localStorage.getItem(key);
      if (snapshotCache?.raw === raw) return snapshotCache.value;
      const parsed = raw == null ? undefined : parse(raw);
      const value = parsed ?? defaultValue;
      snapshotCache = {
        raw,
        value
      };
      return value;
    } catch {
      // Storage blocked — fall through to default.
      return defaultValue;
    }
  }

  /** Writes the value to localStorage and dispatches the custom change event to notify in-tab subscribers. Logs a warning when the write fails so storage-blocked environments surface feedback instead of silently dropping updates. */
  function set(value: T): void {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(key, serialize(value));
    } catch (error: unknown) {
      // biome-ignore lint/suspicious/noConsole: localStorage write failure must be visible — silent swallow is a diagnosed UX bug
      console.warn(`[localStorage] Failed to write key "${key}"`, error);
    }
    window.dispatchEvent(new Event(eventName));
  }

  /** Registers listeners for both in-tab custom events and cross-tab native storage events, returning an unsubscribe function for cleanup. */
  function subscribe(onChange: () => void): () => void {
    if (typeof window === 'undefined') return () => undefined;
    /** Handles native cross-tab storage events, filtering to only trigger onChange when the key matches this store's key. */
    const onStorage = (event: StorageEvent) => {
      if (event.key === key || event.key == null) onChange();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(eventName, onChange);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(eventName, onChange);
    };
  }

  /** React hook that subscribes to the store via `useSyncExternalStore`, using the configured default as the SSR snapshot for hydration safety. */
  function useValue() {
    const ssrSnapshot = _temp;
    return useSyncExternalStore(subscribe, read, ssrSnapshot);
  }
  function _temp() {
    return defaultValue;
  }
  return {
    read,
    set,
    subscribe,
    useValue
  };
}